import { doc, DocumentData, getDoc } from 'firebase/firestore';
import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Popover, Button, message } from 'antd';
import { getPrescription } from '../../../store/selectors/prescriptionPad';
import { firestore } from '../../../firestore';
import getTeleLinksWhenPlatformIsEka from '../../../api/getTeleLinksWhenPlatformIsEka';
import { AntTooltip } from '../../common/AntTooltip';
import { track } from '../../../tracking';
import { METROPOLIS_TELE_HOST } from '../../../api/constants';
import { getMe } from '../../../store/idbHandler';
import { writeTeleV2UrlsInFS } from '../../../firestore/appointments';
export const openTeleWindow = (url: string): Window | null => {
    try {
        const width = 1053; // reason being in case of virtual BG it doesent render live preview on pre call screen if window is too small
        const height = 950;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;

        return window.open(
            url,
            'Eka care - Video call',
            `width=${width},height=${height},top=${top},left=${left}`,
        );
    } catch (e) {
        console.error(e);
        return null;
    }
};

const VideoCallBt = ({
    isPadV3,
    aid,
    ptName,
}: {
    isPadV3?: boolean;
    aid: string;
    ptName: string;
}) => {
    const [isVCOn, setVCOn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingCopyButton, setIsLoadingCopyButton] = useState(false);
    const [guestJoiningLink, setGuestJoiningLink] = useState<string | undefined>(undefined);
    const [hostJoiningLink, setHostJoiningLink] = useState<string | null>(null);
    const [popoverVisible, setPopoverVisible] = useState(false);
    const vcRef = useRef<Window | null>(null);
    const prescription = useSelector(getPrescription);
    const businessId = getMe()?.business?.id || '';

    const copyToClipboard = async (text: string) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (err) {}

        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;

            textarea.style.position = 'fixed';
            textarea.style.top = '0';
            textarea.style.left = '0';
            textarea.style.opacity = '0';

            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textarea);

            return successful;
        } catch (err) {
            console.error('Copy to clipboard failed:', err);
            return false;
        }
    };

    const generateFreshEkaLinksForTele = async () => {
        const apiLinks = await getTeleLinksWhenPlatformIsEka(aid);
        return {
            meet_link: apiLinks?.guest_url,
            host_link: apiLinks?.host_url,
        };
    };

    const handleIconClick = () => {
        if (vcRef.current && !vcRef.current.closed) {
            vcRef.current.focus();
            return;
        }
        setPopoverVisible(true);
    };

    const hostAndGuestUrlsExistingOrOnDemand = async (data: DocumentData) => {
        let urls;
        if (data.mode === 'tele') {
            const platform = data?.vc_meta?.platform;

            if (platform === 'other') {
                urls = data?.vc_meta;
                return {
                    meet_link: urls.meet_link as string,
                    platform,
                };
            } else if (platform === '100ms') {
                urls = data?.vc_meta;
                return {
                    meet_link: urls.meet_link as string,
                    host_link: urls.host_link as string,
                    platform,
                };
            } else {
                urls = data?.vc_meta;
                let finalWriteToFS = false;
                if (!urls?.meet_link) {
                    urls = await generateFreshEkaLinksForTele();
                    finalWriteToFS = true;
                }
                return {
                    meet_link: urls.meet_link as string,
                    host_link: urls.host_link as string,
                    platform: 'eka',
                    flagForWriteToFS: finalWriteToFS,
                };
            }
        } else {
            // SOME CONTEXT!!
            // 1 good thing about HMS is if we send the same room name it gives the same URLS
            // which was not the case in daily.co.
            // hence even if the doc clicks on this icon multiple times we dont have to read our FS
            // neither at /appointments/(aid)/tele/(aid)
            // nor do we need to update /appointments/aid from tryst and make a new vc_meta object
            // for the `in_clinic` ones coz the AID is same and even if the doc clicks on the icon
            // he'll always have the same URL for that PT.

            // and once doc joins here, through webhook I'll get a hit on teleBE repo — which hits tryst
            // and that dumps in /appointments/(aid)/tele/(aid)/events/(aid) for whatsapp and the PT receives the WA message

            // hence neither do we need to write vc_meta for in_clinic PTS in /appointments/aid
            // nor read it in this else if condition!
            urls = await generateFreshEkaLinksForTele();
            return {
                meet_link: urls.meet_link as string,
                host_link: urls.host_link as string,
                flagForWriteToFS: true,
                platform: 'eka', // when mode is in_clinic but the tele call was made on demand, we need to write the urls to /tele so that when sending the WA message the guest url is picked up
            };
        }
    };

    const handleStartCall = async () => {
        if (!aid) return;

        if (vcRef.current && !vcRef.current.closed) {
            vcRef.current.focus();
            return;
        }

        if (hostJoiningLink) {
            vcRef.current = openTeleWindow(hostJoiningLink);
            return;
        }
        setIsLoading(true);
        try {
            const snap = await getDoc(doc(firestore, 'appointments', aid));

            if (!snap.exists()) return;

            const data = snap.data();

            const url = await hostAndGuestUrlsExistingOrOnDemand(data);

            if (url) {
                if (url.platform === 'other') {
                    setGuestJoiningLink(url.meet_link);
                    setHostJoiningLink(url.meet_link);
                    vcRef.current = openTeleWindow(url.meet_link);
                } else {
                    if (url?.platform === 'eka') {
                        if (url.host_link) {
                            if (!url.host_link.includes('?name=')) {
                                setGuestJoiningLink(urlFormatter(url.meet_link, 'name', ptName));
                                const myProfile = getMe();

                                const doc = myProfile?.docs?.find(
                                    (item) => item?.id == data?.doctor_oid,
                                );
                                console.info(
                                    data?.doctor_oid,
                                    'doc oid',
                                    'writing',
                                    url.flagForWriteToFS,
                                );

                                const scopedHostlink = urlFormatter(
                                    url.host_link,
                                    'name',
                                    `Dr ${
                                        [doc?.personal?.name?.f, doc?.personal?.name?.l]
                                            .filter(Boolean)
                                            .join(' ') || (getMe()?.user.fn as string)
                                    }`,
                                );
                                vcRef.current = openTeleWindow(scopedHostlink);
                                setHostJoiningLink(scopedHostlink);
                                if (url?.flagForWriteToFS) {
                                    await writeTeleV2UrlsInFS(
                                        aid,
                                        scopedHostlink,
                                        urlFormatter(url.meet_link, 'name', ptName),
                                        true,
                                    );
                                }
                            } else {
                                setGuestJoiningLink(url.meet_link);
                                setHostJoiningLink(url.host_link);
                                vcRef.current = openTeleWindow(url.host_link);
                                if (url?.flagForWriteToFS) {
                                    await writeTeleV2UrlsInFS(
                                        aid,
                                        url.host_link,
                                        url.meet_link,
                                        true,
                                    );
                                }
                            }
                        }
                    } else {
                        setGuestJoiningLink(url.meet_link);
                        setHostJoiningLink(url.host_link as string);
                        vcRef.current = openTeleWindow(url.host_link as string);
                    }
                }
                setVCOn(true);
                setPopoverVisible(false);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.videoclose = () => {
                    vcRef.current = null;
                    setVCOn(false);
                };
            }
            track('START_CALL_BUTTON_CLICK_TELE_V2_FLOW');
        } catch (err) {
            console.error('Error fetching Firestore or links', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyLink = async () => {
        let linkToCopy = guestJoiningLink;
        if (!linkToCopy) {
            setIsLoadingCopyButton(true);
            try {
                const snap = await getDoc(doc(firestore, 'appointments', aid));
                if (!snap.exists()) {
                    message.error('Failed to get meeting link');
                    return;
                }

                const data = snap.data();

                const urls = await hostAndGuestUrlsExistingOrOnDemand(data)!;

                if (urls?.meet_link) {
                    if (urls.platform === 'eka') {
                        if (!urls.meet_link.includes('?name=')) {
                            const scopedMeetLink = urlFormatter(urls.meet_link, 'name', ptName);
                            setGuestJoiningLink(scopedMeetLink);
                            linkToCopy = scopedMeetLink;
                        } else {
                            setGuestJoiningLink(urls?.meet_link);
                            linkToCopy = urls?.meet_link;
                        }
                    } else {
                        setGuestJoiningLink(urls?.meet_link);
                        linkToCopy = urls?.meet_link;
                    }
                }
            } catch (err) {
                console.error('Error fetching link for copy', err);
                message.error('Failed to get meeting link');
                return;
            } finally {
                setIsLoadingCopyButton(false);
            }
        }

        if (linkToCopy) {
            try {
                const teleLink =
                    businessId === '74117920305567'
                        ? `${METROPOLIS_TELE_HOST}?apptid=${aid}`
                        : linkToCopy;
                copyToClipboard(teleLink);
                message.success('Meeting link copied to clipboard');
                setPopoverVisible(false);
            } catch (err) {
                message.error('Failed to copy link');
            }
        }
        track('CLICK_ON_PAD_TO_COPY_PATIENT_TELE_LINK');
    };

    const urlFormatter = (baseUrl: string, QPkey: string, QPvalue: string) => {
        try {
            const cleaned = new URL(baseUrl);
            cleaned.searchParams.set(QPkey, QPvalue);
            return cleaned.toString();
        } catch (e) {
            return baseUrl;
        }
    };
    // Sync status on tab focus
    useEffect(() => {
        const checkFocus = () => {
            setTimeout(() => {
                setVCOn(vcRef.current !== null && !vcRef.current.closed);
            }, 500);
        };
        window.addEventListener('focus', checkFocus);
        return () => window.removeEventListener('focus', checkFocus);
    }, []);

    useEffect(() => {
        setVCOn(vcRef.current !== null && !vcRef.current.closed);
    }, [prescription]);

    const popoverContent = (
        <div className="flex gap-16 px-3 py-2 bg-white rounded-md shadow-md">
            <Button
                type="primary"
                onClick={handleStartCall}
                disabled={isLoading}
                className="flex items-center justify-center rounded-4 w-120 bg-darwin-accent-bright-blue-800 px-8 h-28 text-white font-700"
            >
                {isLoading ? <Spinner /> : 'Start Call'}
            </Button>
            <Button
                disabled={isLoadingCopyButton}
                onClick={handleCopyLink}
                className="secondary-btn w-120 flex items-center justify-center rounded-4 px-8 h-28 text-white"
            >
                {isLoadingCopyButton ? <Spinner /> : 'Copy Link'}
            </Button>
        </div>
    );

    if (isPadV3) {
        return (
            <Popover
                content={popoverContent}
                title=""
                trigger="click"
                visible={popoverVisible}
                onVisibleChange={setPopoverVisible}
                placement="bottom"
            >
                <AntTooltip placement="bottom" content={'Start Video Call'}>
                    <span
                        onClick={handleIconClick}
                        className="bg-darwin-accent-green-200 h-36 w-36 justify-center flex items-center rounded-4 cursor-pointer"
                    >
                        <svg
                            role="img"
                            viewBox="0 0 14 9"
                            xmlns="http://www.w3.org/2000/svg"
                            className={`w-14 h-12 fill-darwin-accent-green-900 ${
                                isVCOn ? 'video-animate-flicker' : ''
                            }`}
                        >
                            <path d="M9.25 1.125V7.875C9.25 8.50781 8.73438 9 8.125 9H1.375C0.742188 9 0.25 8.50781 0.25 7.875V1.125C0.25 0.515625 0.742188 0 1.375 0H8.125C8.73438 0 9.25 0.515625 9.25 1.125ZM13.75 1.5V7.52344C13.75 8.10938 13.0469 8.46094 12.5547 8.13281L10 6.35156V2.67188L12.5547 0.890625C13.0469 0.5625 13.75 0.914062 13.75 1.5Z"></path>
                        </svg>
                    </span>
                </AntTooltip>
            </Popover>
        );
    }

    return (
        <Popover
            content={popoverContent}
            title="Video Call Options"
            trigger="click"
            visible={popoverVisible}
            onVisibleChange={setPopoverVisible}
            placement="bottomRight"
        >
            <div onClick={handleIconClick} className="menuitem">
                <div>
                    <div>
                        {' '}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 576 512"
                            width="18"
                            height="18"
                        >
                            <path d="M543.9 96c-6.2 0-12.5 1.8-18.2 5.7L416 171.6v-59.8c0-26.4-23.2-47.8-51.8-47.8H51.8C23.2 64 0 85.4 0 111.8v288.4C0 426.6 23.2 448 51.8 448h312.4c28.6 0 51.8-21.4 51.8-47.8v-59.8l109.6 69.9c5.7 4 12.1 5.7 18.2 5.7 16.6 0 32.1-13 32.1-31.5v-257c.1-18.5-15.4-31.5-32-31.5zM384 400.2c0 8.6-9.1 15.8-19.8 15.8H51.8c-10.7 0-19.8-7.2-19.8-15.8V111.8c0-8.6 9.1-15.8 19.8-15.8h312.4c10.7 0 19.8 7.2 19.8 15.8v288.4zm160-15.7l-1.2-1.3L416 302.4v-92.9L544 128v256.5z"></path>
                        </svg>
                    </div>
                    <p>Video Call</p>
                </div>
                <div className="flex space-x-8">
                    {isVCOn && (
                        <>
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 12 12"
                                xmlns="http://www.w3.org/2000/svg"
                                className="video-animate-flicker"
                            >
                                <path
                                    d="M11.5111 8.48572L9.10482 7.45447C9.04036 7.43298 8.97591 7.4115 8.88997 7.4115C8.73958 7.4115 8.58919 7.49744 8.50326 7.60486L7.42904 8.91541C5.75326 8.12048 4.42122 6.78845 3.6263 5.11267L4.93685 4.03845C5.04427 3.95251 5.13021 3.80212 5.13021 3.63025C5.13021 3.5658 5.10872 3.50134 5.08724 3.43689L4.05599 1.03064C3.97005 0.83728 3.77669 0.708374 3.56185 0.708374C3.54036 0.708374 3.4974 0.729858 3.45443 0.729858L1.22005 1.24548C0.983724 1.30994 0.833333 1.5033 0.833333 1.73962C0.833333 7.26111 5.2806 11.7084 10.8021 11.7084C11.0384 11.7084 11.2318 11.558 11.2962 11.3217L11.8118 9.08728C11.8118 9.04431 11.8118 9.00134 11.8118 8.97986C11.8118 8.76501 11.6829 8.57166 11.5111 8.48572Z"
                                    fill="#003ba8"
                                />
                            </svg>
                        </>
                    )}
                    <svg
                        width="25"
                        height="25"
                        viewBox="0 0 28 28"
                        className="fill-neutral-5 cursor-pointer"
                        preserveAspectRatio="xMidYMid meet"
                    >
                        <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                    </svg>
                </div>
            </div>
        </Popover>
    );
};

const Spinner = () => {
    return (
        <svg
            className="w-16 h-16 fill-white animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
        >
            <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
        </svg>
    );
};

export default VideoCallBt;
